/* AW26 Shop de look - page interaction
   Opens a look in a modal <dialog> and adds the toggled-on products of that
   look to the cart in one go. The dialog is the PDP "Shop the fit" module in a
   window: per-row include/exclude toggles, a running total and one add button,
   so this file mirrors that module's script (sections/shop-the-fit.liquid):
   POST /cart/add.js with every selected variant, then dispatch a `cart:update`
   event so the theme's cart drawer refreshes.
   No external libraries; motion is CSS (transform/opacity). */
(function () {
  'use strict';

  /* ── Money ────────────────────────────────────────────────────────────────
     Same formatter as the PDP module: nl-NL, the shop's active currency. */
  function formatMoney(cents) {
    var currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: currency }).format(
        cents / 100
      );
    } catch (err) {
      return '€ ' + (cents / 100).toFixed(2).replace('.', ',');
    }
  }

  /* The variant a row will add: the chosen size chip, or - when the product has
     a single variant and renders no chips - the row's own default. */
  function rowVariantId(row) {
    var chip = row.querySelector('[data-sdl-size][aria-pressed="true"]:not([disabled])');
    if (chip) return chip.getAttribute('data-variant-id');
    return row.getAttribute('data-variant-id');
  }

  function isRowOn(row) {
    var toggle = row.querySelector('[data-sdl-toggle]');
    return !!(toggle && toggle.checked);
  }

  /* ── Total + CTA state ── mirrors updateTotals() in the PDP module ──────── */
  function updateTotals(dialog) {
    var total = 0;
    var count = 0;

    dialog.querySelectorAll('[data-sdl-product]').forEach(function (row) {
      if (!isRowOn(row) || !rowVariantId(row)) return;
      total += parseInt(row.getAttribute('data-price'), 10) || 0;
      count += 1;
    });

    var totalEl = dialog.querySelector('[data-sdl-total]');
    if (totalEl) totalEl.textContent = formatMoney(total);

    var addBtn = dialog.querySelector('[data-sdl-add-fit]');
    if (addBtn && !addBtn.dataset.sdlBusy) addBtn.disabled = count === 0;
  }

  /* The size strip wraps in CSS now (no horizontal scroll), so opening a
     dialog or resizing the window only needs to keep the total/CTA in sync. */
  function syncDialog(dialog) {
    updateTotals(dialog);
  }

  /* ── Dialog ───────────────────────────────────────────────────────────── */
  function openDialog(trigger) {
    var id = trigger.getAttribute('aria-controls');
    if (!id) return;
    var dialog = document.getElementById(id);
    if (!dialog) return;

    dialog._sdlTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
      dialog.setAttribute('aria-modal', 'true');
    }

    syncDialog(dialog);
  }

  function closeDialog(dialog) {
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  function onDialogClose(dialog) {
    var trigger = dialog._sdlTrigger;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (typeof trigger.focus === 'function') trigger.focus();
    }
  }

  function selectSize(sizeBtn) {
    var row = sizeBtn.closest('[data-sdl-product]');
    if (!row) return;
    row.querySelectorAll('[data-sdl-size]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', 'false');
    });
    sizeBtn.setAttribute('aria-pressed', 'true');

    var dialog = row.closest('[data-sdl-dialog]');
    if (dialog) updateTotals(dialog);
  }

  function toggleRow(toggle) {
    var row = toggle.closest('[data-sdl-product]');
    if (!row) return;
    row.classList.toggle('is-selected', toggle.checked);

    var dialog = row.closest('[data-sdl-dialog]');
    if (dialog) updateTotals(dialog);
  }

  function getCartSections() {
    var component = document.querySelector('cart-items-component, cart-drawer-component, #cart-drawer');
    var sectionId = component && component.dataset ? component.dataset.sectionId : null;
    return sectionId ? [sectionId] : [];
  }

  /* ── Add the fit ── mirrors the PDP module's add-all handler ───────────── */
  function addFit(addBtn) {
    var dialog = addBtn.closest('[data-sdl-dialog]');
    if (!dialog || addBtn.disabled) return;

    var items = [];
    dialog.querySelectorAll('[data-sdl-product]').forEach(function (row) {
      if (!isRowOn(row)) return;
      var variantId = rowVariantId(row);
      if (variantId) items.push({ id: parseInt(variantId, 10), quantity: 1 });
    });

    if (items.length === 0) return;

    var textEl = addBtn.querySelector('[data-sdl-add-fit-text]');
    var labelDefault = addBtn.getAttribute('data-label-default') || 'Voeg de fit toe aan winkelwagen';

    function setText(attr, fallback) {
      if (textEl) textEl.textContent = addBtn.getAttribute(attr) || fallback;
    }

    addBtn.dataset.sdlBusy = 'true';
    addBtn.disabled = true;
    setText('data-label-loading', 'Bezig...');

    function restore() {
      if (textEl) textEl.textContent = labelDefault;
      delete addBtn.dataset.sdlBusy;
      updateTotals(dialog);
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: getCartSections().join(','),
        sections_url: window.location.pathname
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add-to-cart-failed');
        return res.json();
      })
      .then(function (state) {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: state,
              sourceId: 'shop-de-look-page',
              data: {
                source: 'shop-de-look-page',
                itemCount: items.length,
                sections: state.sections || {}
              }
            }
          })
        );

        setText('data-label-added', 'Toegevoegd');
        window.setTimeout(restore, 2000);
      })
      .catch(function () {
        setText('data-label-error', 'Er ging iets mis');
        window.setTimeout(restore, 2000);
      });
  }

  function init() {
    // Open triggers
    document.querySelectorAll('[data-sdl-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        openDialog(trigger);
      });
    });

    // Dialog-level behaviour: backdrop click, native close event (Escape included)
    document.querySelectorAll('[data-sdl-dialog]').forEach(function (dialog) {
      dialog.addEventListener('close', function () {
        onDialogClose(dialog);
      });

      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) {
          closeDialog(dialog);
        }
      });

      // Totals are correct before the first open, so the CTA is never a live
      // button with a stale price behind it.
      updateTotals(dialog);
    });

    // Delegated controls inside dialogs
    document.addEventListener('click', function (event) {
      var closeBtn = event.target.closest('[data-sdl-close]');
      if (closeBtn) {
        var dialog = closeBtn.closest('[data-sdl-dialog]');
        if (dialog) closeDialog(dialog);
        return;
      }

      var sizeBtn = event.target.closest('[data-sdl-size]');
      if (sizeBtn && !sizeBtn.disabled) {
        selectSize(sizeBtn);
        return;
      }

      var addBtn = event.target.closest('[data-sdl-add-fit]');
      if (addBtn) {
        addFit(addBtn);
      }
    });

    document.addEventListener('change', function (event) {
      var toggle = event.target.closest('[data-sdl-toggle]');
      if (toggle) toggleRow(toggle);
    });

    window.addEventListener('resize', function () {
      document.querySelectorAll('[data-sdl-dialog][open]').forEach(syncDialog);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
